/**
 * WF-107E · Operational Coverage widget on the Workforce Operations
 * Dashboard (PO 2026-02-04, Wave 3).
 *
 * A single compact card designed so a manager can answer within
 * seconds:
 *   • Who is on call right now?
 *   • When does coverage change next, and to whom?
 *   • What temporary changes (replacements / swaps) are booked?
 *
 * Data source: three parallel GETs to the WF-107A/B backend.
 *
 * Alerts surfaced (V1):
 *   • Coverage ending within <24h.
 *   • Active replacements upcoming.
 *   • Active swaps upcoming.
 * Future alert stubs referenced in the PRD (conflicts, missing) are
 * documented in-line and can be enabled without a schema change.
 *
 * Architecture stubs — the widget does NOT integrate with these today
 * but the callback surface is here so the Phone / SMS / Teams / AI /
 * Dispatch wiring drops in without a redesign:
 *   • ``onCallDriver(rotation)``  — future click-to-dial (Phone)
 *   • ``onNotifyDriver(rotation)`` — future SMS / Teams / AI escalation
 *   • ``onOpenDispatch(rotation)`` — future Dispatch Dashboard deep-link
 */
import { useCallback, useEffect, useState } from "react";
import {
  Phone, PaperPlaneTilt, ShieldCheck, ArrowRight,
  CalendarBlank, WarningCircle,
} from "@phosphor-icons/react";
import { workforceApi } from "./workforceApi";

const INITIALS = (name) => (name || "?")
  .split(/\s+/).map(s => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";

const FRIENDLY = (iso) => {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric" });
};

const RANGE = (startIso, endIso) => {
  if (!startIso || !endIso) return "—";
  const end = new Date(`${endIso}T00:00:00`);
  end.setDate(end.getDate() - 1);
  return `${FRIENDLY(startIso)} – ${FRIENDLY(end.toISOString().slice(0, 10))}`;
};

const HOURS_UNTIL = (iso) => {
  if (!iso) return null;
  const t = new Date(`${iso}T00:00:00`).getTime();
  return Math.round((t - Date.now()) / (1000 * 60 * 60));
};

export default function CoverageWidget({ onNavigateTab }) {
  const [state, setState] = useState({ loading: true, current: null, next: null, upcoming: [], error: null });

  const load = useCallback(async () => {
    try {
      const [c, up] = await Promise.all([
        workforceApi.coverageCurrent(),
        workforceApi.coverageUpcoming(4),
      ]);
      setState({
        loading: false,
        current: c.data?.current || null,
        next: c.data?.next || null,
        upcoming: up.data?.items || [],
        error: null,
      });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: e?.response?.data?.detail || "Could not load coverage" }));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const { current, next, upcoming, loading, error } = state;

  // ---- Alerts ---------------------------------------------------------
  const alerts = [];
  const currentEndsIn = current?.end_date ? HOURS_UNTIL(current.end_date) : null;
  if (currentEndsIn !== null && currentEndsIn <= 24 && currentEndsIn >= 0) {
    alerts.push({ tone: "amber", label: `Coverage handoff in ${currentEndsIn}h` });
  }
  const activeReplacements = upcoming.filter(r => r.source === "replacement").length;
  const activeSwaps        = upcoming.filter(r => r.source === "swap").length;
  if (activeReplacements) alerts.push({ tone: "amber", label: `${activeReplacements} active replacement${activeReplacements > 1 ? "s" : ""}` });
  if (activeSwaps)        alerts.push({ tone: "violet", label: `${activeSwaps} active swap${activeSwaps > 1 ? "s" : ""}` });
  // Future stubs (documented, not enabled): missing coverage, conflicts.

  return (
    <section
      data-testid="wf-ops-coverage"
      className="rounded-lg border border-slate-200 bg-white shadow-sm"
    >
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-3">
        <div>
          <h3 className="font-display text-sm font-semibold uppercase tracking-wider text-slate-500">
            On-Call Coverage
          </h3>
          <p className="text-[11px] italic text-slate-400">
            Current and upcoming after-hours coverage.
          </p>
        </div>
        <button
          type="button"
          data-testid="wf-ops-coverage-open-workspace"
          onClick={() => onNavigateTab?.("coverage")}
          className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
        >
          Open Workspace <ArrowRight size={11} weight="bold" />
        </button>
      </header>

      {loading && (
        <div className="px-5 py-6 text-sm text-slate-500">Loading coverage…</div>
      )}
      {error && !loading && (
        <div className="px-5 py-4 text-sm text-status-error-fg">{error}</div>
      )}

      {!loading && !error && (
        <>
          <div className="grid gap-4 border-b border-slate-100 px-5 py-4 md:grid-cols-2">
            {/* Current */}
            <div className="rounded-md border border-slate-800 bg-charcoal p-4 text-ink-inverse">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Current On-Call</div>
              {current ? (
                <>
                  <div className="mt-2 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-sm font-semibold text-charcoal">
                      {INITIALS(current.employee_name)}
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-base font-semibold" data-testid="wf-ops-coverage-current-name">
                        {current.employee_name}
                      </div>
                      <div className="text-xs text-slate-300">{RANGE(current.start_date, current.end_date)}</div>
                    </div>
                  </div>
                  {current.source !== "generated" && current.original_employee_name && (
                    <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-semibold text-amber-200">
                      <ShieldCheck size={10} weight="bold" />
                      Differs from standard ({current.original_employee_name})
                    </div>
                  )}
                </>
              ) : (
                <div className="mt-3 text-sm text-slate-400">No coverage assigned for today.</div>
              )}
            </div>
            {/* Next */}
            <div className="rounded-md border border-slate-200 bg-white p-4">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Next Rotation</div>
              {next ? (
                <>
                  <div className="mt-2 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-sm font-semibold text-white">
                      {INITIALS(next.employee_name)}
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-base font-semibold text-slate-900" data-testid="wf-ops-coverage-next-name">
                        {next.employee_name}
                      </div>
                      <div className="text-xs text-slate-600">Starts {FRIENDLY(next.start_date)}</div>
                    </div>
                  </div>
                  {next.source !== "generated" && next.original_employee_name && (
                    <div className="mt-2 inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                      <ShieldCheck size={10} weight="bold" />
                      {next.source === "replacement" ? "Replacement" : "Swap"} · standard {next.original_employee_name}
                    </div>
                  )}
                </>
              ) : (
                <div className="mt-3 text-sm text-slate-500">No upcoming handoff scheduled.</div>
              )}
            </div>
          </div>

          {/* Alerts */}
          {alerts.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 bg-slate-50 px-5 py-2 text-xs">
              <WarningCircle size={12} weight="fill" className="text-amber-500" />
              <span className="font-semibold text-slate-600">Attention:</span>
              {alerts.map((a, i) => (
                <span
                  key={i}
                  data-testid={`wf-ops-coverage-alert-${a.tone}`}
                  className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                    a.tone === "amber"  ? "border border-amber-200 bg-amber-50 text-amber-800"
                  : a.tone === "violet" ? "border border-violet-200 bg-violet-50 text-violet-800"
                  : "border border-slate-200 bg-white text-slate-700"
                  }`}
                >
                  {a.label}
                </span>
              ))}
            </div>
          )}

          {/* Upcoming mini-list */}
          <ul className="divide-y divide-slate-100" data-testid="wf-ops-coverage-upcoming">
            {upcoming.length === 0 && (
              <li className="px-5 py-3 text-sm text-slate-500">
                <CalendarBlank size={13} weight="regular" className="mr-1 inline" />
                No upcoming rotations.
              </li>
            )}
            {upcoming.map((r) => (
              <li key={r.id} className="flex items-center gap-3 px-5 py-2 text-sm">
                <RotationDot source={r.source} />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-slate-900">{r.employee_name}</div>
                  <div className="text-[11px] text-slate-500">
                    {RANGE(r.start_date, r.end_date)}
                    {r.source !== "generated" && r.original_employee_name && (
                      <span className="ml-2 text-[10px] italic text-slate-500">
                        (was {r.original_employee_name})
                      </span>
                    )}
                  </div>
                </div>
                {/* Future integration hooks — kept as disabled buttons so
                    the layout locks in now; wiring flips on when Phone /
                    SMS / Teams / AI / Dispatch integrations ship. */}
                <FutureButton icon={<Phone size={11} weight="regular" />}         title="Call driver (future)" />
                <FutureButton icon={<PaperPlaneTilt size={11} weight="regular" />} title="Notify driver (future)" />
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Sub-components                                                      */
/* ------------------------------------------------------------------ */

function RotationDot({ source }) {
  const map = {
    generated:   "bg-blue-500",
    replacement: "bg-orange-500",
    swap:        "bg-violet-600",
  };
  return <span className={`inline-block h-2 w-2 rounded-full ${map[source] || map.generated}`} />;
}

function FutureButton({ icon, title }) {
  return (
    <button
      type="button"
      disabled
      title={title}
      className="rounded-md border border-slate-200 bg-white p-1 text-slate-400 opacity-60"
      data-testid={`wf-ops-coverage-future-${(title || "").split(" ")[0].toLowerCase()}`}
    >
      {icon}
    </button>
  );
}
